import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { parseWSMessage } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { getCallQueueMaxAge } from '@rsos/utils/metaTags';
import {
  setStaticSpatialObjectActiveStatus,
  setStaticSpatialObjectInactiveStatus,
} from '../../../actions/spatialLayers';
import {
  selectSupportedSpatialLayerOrgs,
  useHasAccessToFeature,
} from '../hooks/useHandleActiveSpatialObject';

/**
 * Hook that accesses StreamWS to handle setting a spatial object's status
 * through the use of an additional callback function. This in turn changes the
 * spatial object's polygon color. Assumes user has access to the data,
 * otherwise it won't be sent through the websocket. Capabilities and
 * permissions are checked to safeguard against unexpected UI occurrences.
 */
const useHandleActiveSpatialObject = () => {
  const StreamWS = window.__RSOS?.streamWS?.instance;

  const dispatch = useDispatch();

  const hasAccessToFeature: Record<string, boolean> = useHasAccessToFeature();

  const supportedSpatialLayerOrgs = useSelector(
    selectSupportedSpatialLayerOrgs,
  );

  const pendingReversions = useRef<Set<ReturnType<typeof setTimeout>>>(
    new Set(),
  );

  const handleSpatialObjectActiveStatus = useCallback(
    (message: Record<string, unknown>) => {
      const parsedMessageType = parseWSMessage(message);
      const orgName = parsedMessageType.organization;
      if (
        orgName &&
        hasAccessToFeature[orgName] &&
        supportedSpatialLayerOrgs.includes(orgName)
      ) {
        dispatch(setStaticSpatialObjectActiveStatus({ data: message?.body }));

        const timeoutId = setTimeout(
          () => {
            pendingReversions.current.delete(timeoutId);
            dispatch(
              setStaticSpatialObjectInactiveStatus({ data: message?.body }),
            );
          },
          Number(getCallQueueMaxAge()) * 1000,
        );
        pendingReversions.current.add(timeoutId);
      }
    },
    [dispatch, hasAccessToFeature, supportedSpatialLayerOrgs],
  );

  useEffect(() => {
    const pending = pendingReversions.current;
    return () => {
      pending.forEach(clearTimeout);
      pending.clear();
    };
  }, []);

  useEffect(() => {
    if (StreamWS) {
      StreamWS.addOnMessageCallback({
        handleSpatialObjectActiveStatus: handleSpatialObjectActiveStatus,
      });

      return () => {
        StreamWS.removeOnMessageCallback('handleSpatialObjectActiveStatus');
      };
    }
  }, [StreamWS, handleSpatialObjectActiveStatus]);

  return null;
};

export default useHandleActiveSpatialObject;
